import type { GlobalAsset } from '@types';
import { addressSubdivisions, subdivisionValue } from '../common/addressSubdivisions';
import { dashboardCountry } from './clientDashboardCountries';

export function selectableAssetLocations(assets: GlobalAsset[]): GlobalAsset[] {
    return assets.filter(asset => asset.location?.address?.trim())
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.name.localeCompare(b.name));
}

const postalCodePattern = /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9 -]{1,11}$/;

function legacyAddressParts(address: string, country: string) {
    const segments = address.split(',').map(part => part.trim()).filter(Boolean);
    const knownCountry = dashboardCountry(country);
    if (knownCountry && dashboardCountry(segments.at(-1))?.iso3 === knownCountry.iso3)
        segments.pop();

    let postalCode = '';
    if (segments.length > 1 && postalCodePattern.test(segments.at(-1) || ''))
        postalCode = segments.pop() || '';

    let administrativeArea = '';
    const regions = addressSubdivisions(country);
    const last = segments.at(-1)?.toLowerCase() || '';
    if (segments.length > 2) {
        const region = regions.find(item => item.name.toLowerCase() === last || item.code.toLowerCase() === last || item.code.split('-').pop()?.toLowerCase() === last);
        if (region)
            administrativeArea = segments.pop() || '';
    }

    const city = segments.length > 1 ? segments.pop() || '' : '';
    return { streetAddress: segments.join(', '), city, administrativeArea, postalCode };
}

export function assetLocationValues(asset: GlobalAsset) {
    const address = asset.location?.address?.trim() || '';
    const parts = asset.location?.addressParts;
    const country = dashboardCountry(parts?.country)?.name
        || dashboardCountry(asset.country)?.name
        || dashboardCountry(asset.iso3)?.name
        || dashboardCountry(address.split(',').at(-1))?.name
        || '';
    const legacy = legacyAddressParts(address, country);
    const city = parts?.city?.trim() || legacy.city;
    const streetAddress = parts?.city?.trim() ? parts.streetAddress.trim() : legacy.streetAddress || parts?.streetAddress?.trim() || '';
    const administrativeArea = subdivisionValue(country, parts?.administrativeArea?.trim() || legacy.administrativeArea);
    const postalCode = parts?.postalCode?.trim() || legacy.postalCode;
    const lat = asset.location?.lat;
    const lng = asset.location?.lng;
    const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);

    return {
        address: streetAddress,
        country,
        state: administrativeArea,
        city,
        zip: postalCode,
        lat: hasCoordinates ? lat! : '',
        lng: hasCoordinates ? lng! : '',
    };
}

export function assetLocationFields(asset: GlobalAsset, prefix: string): Record<string, string | number> {
    const location = assetLocationValues(asset);
    if (!prefix)
        return location;
    return Object.fromEntries(Object.entries(location).map(([name, value]) => [`${prefix}_${name}`, value]));
}
